<template>
  <div>
    <div class="group flex items-center gap-2.5 pr-3 py-3 hover:bg-gray-50/70 dark:hover:bg-gray-800/50 transition-colors"
      :style="{ paddingLeft: `${16 + depth * 20}px` }">

      <button v-if="hasChildSlot" type="button" @click="expanded = !expanded"
        class="w-5 h-5 flex-shrink-0 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
        <svg class="w-3.5 h-3.5 transition-transform duration-150" :class="expanded ? 'rotate-90' : ''"
          fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
        </svg>
      </button>
      <span v-else class="w-5 h-5 flex-shrink-0"></span>

      <div class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" :class="config.iconBg">
        <svg class="w-3.5 h-3.5" :class="config.iconColor" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" :d="config.iconPath"/>
        </svg>
      </div>

      <div class="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
        <span class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{{ node.name }}</span>
        <span v-if="hasChildSlot" class="text-xs text-gray-400 dark:text-gray-500 tabular-nums flex-shrink-0">{{ children.length }}</span>
        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 flex-shrink-0">
          {{ config.label }}
        </span>
        <span v-if="node.description" class="text-xs text-gray-400 dark:text-gray-500 truncate hidden sm:inline">{{ node.description }}</span>
      </div>

      <div class="flex items-center gap-0.5 flex-shrink-0">
        <button v-if="config.childType" type="button" title="Add" @click="$emit('action', { action: 'add', type: config.childType, node: null, parent: node })"
          class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
          </svg>
        </button>
        <button type="button" title="Edit" @click="$emit('action', { action: 'edit', type, node, parent })"
          class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
          </svg>
        </button>
        <button type="button" title="Delete" @click="showConfirm = true"
          class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <Teleport to="body">
      <Transition name="modal" appear>
        <div v-if="showConfirm" class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="showConfirm = false">
          <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
          <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-sm w-full p-6 animate-in">
            <div class="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
              </svg>
            </div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white text-center mb-1.5">Delete "{{ node.name }}"?</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
              {{ hasChildSlot ? `This will also delete all ${(config.childLabel || '').toLowerCase()}s inside it.` : 'This action cannot be undone.' }}
            </p>
            <div class="flex gap-3">
              <button @click="showConfirm = false" type="button"
                class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button @click="confirmDelete" type="button"
                class="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Children (animated expand/collapse) -->
    <div v-if="hasChildSlot" class="grid transition-[grid-template-rows] duration-200 ease-out" :style="{ gridTemplateRows: expanded ? '1fr' : '0fr' }">
      <div class="overflow-hidden">
        <p v-if="children.length === 0" class="text-xs text-gray-400 dark:text-gray-500 italic py-2"
          :style="{ paddingLeft: `${16 + (depth + 1) * 20 + 20}px` }">
          No {{ (config.childLabel || '').toLowerCase() }}s yet.
        </p>
        <TransitionGroup name="tree-row" tag="div" class="divide-y divide-gray-100 dark:divide-gray-700/50">
          <TreeRow v-for="child in children" :key="child.id"
            :node="child" :type="config.childType" :parent="node" :depth="depth + 1"
            @action="(payload) => $emit('action', payload)" />
        </TransitionGroup>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { NODE_ICONS } from 'controllers/vue_components/organizations/structure/nodeIcons.js'

const props = defineProps({
  node: { type: Object, required: true },
  type: { type: String, required: true },
  parent: { type: Object, default: null },
  depth: { type: Number, default: 0 },
})

const emit = defineEmits(['action'])

const TYPE_CONFIG = {
  company: {
    label: 'Company',
    childType: 'department',
    childKey: 'departments',
    childLabel: 'Department',
    iconBg: 'bg-blue-50 dark:bg-blue-900/20',
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconPath: NODE_ICONS.company.path,
  },
  department: {
    label: 'Department',
    childType: null,
    childKey: null,
    childLabel: null,
    iconBg: 'bg-gray-100 dark:bg-gray-800',
    iconColor: 'text-gray-500 dark:text-gray-400',
    iconPath: NODE_ICONS.department.path,
  },
}

const expanded = ref(true)
const showConfirm = ref(false)

const config = computed(() => TYPE_CONFIG[props.type])
const hasChildSlot = computed(() => !!config.value.childType)
const children = computed(() => (config.value.childKey ? props.node[config.value.childKey] || [] : []))

function confirmDelete() {
  showConfirm.value = false
  emit('action', { action: 'delete', type: props.type, node: props.node, parent: props.parent })
}
</script>

<style scoped>
.tree-row-enter-active,
.tree-row-leave-active {
  transition: all 0.15s ease;
}
.tree-row-enter-from,
.tree-row-leave-to {
  opacity: 0;
  transform: translateX(-6px);
}
.tree-row-leave-active {
  position: absolute;
}

.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .animate-in { transition: transform 0.2s ease; }
.modal-enter-from .animate-in { transform: scale(0.96) translateY(8px); }
</style>
